/**
 * Tear Sheet PDF Generator
 * 
 * This script enables PDF download functionality for product tear sheets.
 * 
 * Requirements:
 * - jsPDF: https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js
 * - html2canvas: https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js
 * 
 * To enable:
 * 1. Add the library scripts to your theme.liquid file
 * 2. Uncomment the PDF button code in tear-product.liquid
 * 3. Include this script in your theme
 */

(function() {
    'use strict';

    // Wait for DOM and libraries to be ready
    function initPDFGenerator() {
        // Check if required libraries are loaded
        if (typeof window.jspdf === 'undefined') {
            console.warn('jsPDF library not found. PDF download will not be available.');
            return;
        }

        if (typeof html2canvas === 'undefined') {
            console.warn('html2canvas library not found. PDF download will not be available.');
            return;
        }

        const pdfButton = document.getElementById('btn-download-pdf');
        if (!pdfButton) {
            return; // PDF button not found
        }

        // Show the PDF button if libraries are loaded
        if (typeof window.jspdf !== 'undefined' && typeof html2canvas !== 'undefined') {
            pdfButton.style.display = 'inline-block';
        } else {
            // Hide button if libraries aren't available
            pdfButton.style.display = 'none';
            return;
        }

        pdfButton.addEventListener('click', function() {
            const element = document.getElementById('tear-sheet-content');
            if (!element) {
                console.error('Tear sheet content not found');
                return;
            }

            // Get product title from the page
            const titleElement = element.querySelector('.tear-sheet-header h1');
            const productTitle = titleElement ? titleElement.textContent.trim() : 'Product-Tear-Sheet';
            const sanitizedTitle = productTitle.replace(/[^a-z0-9]/gi, '-').toLowerCase();

            // Show loading state
            const originalText = pdfButton.textContent;
            pdfButton.textContent = 'Generating PDF...';
            pdfButton.disabled = true;

            // Hide print button during generation
            const printButton = document.querySelector('.btn-print');
            if (printButton) {
                printButton.style.display = 'none';
            }

            // Preload all images before generating PDF
            function preloadImages() {
                const images = element.querySelectorAll('img');
                const imagePromises = Array.from(images).map(function(img) {
                    if (img.complete) {
                        return Promise.resolve();
                    }
                    return new Promise(function(resolve, reject) {
                        img.onload = resolve;
                        img.onerror = resolve; // Continue even if image fails
                        // Timeout after 5 seconds
                        setTimeout(resolve, 5000);
                    });
                });
                return Promise.all(imagePromises);
            }

            // Wait for images to load, then replace with larger versions for PDF
            preloadImages().then(function() {
                // Store original image sources and replace with larger versions
                const images = element.querySelectorAll('img');
                const originalSources = [];
                
                images.forEach(function(img, index) {
                    originalSources[index] = img.src;
                    const currentSrc = img.src;
                    
                    // Increase image size for PDF - replace width parameters
                    if (currentSrc && (currentSrc.includes('cdn.shopify.com') || currentSrc.includes('shopify'))) {
                        let newSrc = currentSrc
                            .replace(/width=\d+/gi, 'width=300')
                            .replace(/_(\d+)x(\d+)/gi, '_300x300')
                            .replace(/_(\d+)\.(jpg|png|webp)/gi, '_300.$2');
                        
                        if (newSrc !== currentSrc) {
                            img.src = newSrc;
                            img.removeAttribute('srcset');
                        }
                    }
                });
                
                // Wait for larger images to load
                const largerImagePromises = Array.from(images).map(function(img) {
                    if (img.complete) {
                        return Promise.resolve();
                    }
                    return new Promise(function(resolve) {
                        img.onload = resolve;
                        img.onerror = resolve;
                        setTimeout(resolve, 3000);
                    });
                });
                
                return Promise.all(largerImagePromises).then(function() {
                    // Generate PDF with higher scale for better image quality
                    return html2canvas(element, {
                        scale: 3, // Higher quality for better image rendering
                        useCORS: true,
                        allowTaint: false,
                        logging: false,
                        backgroundColor: '#ffffff',
                        windowWidth: element.scrollWidth,
                        windowHeight: element.scrollHeight,
                        imageTimeout: 15000, // Wait up to 15 seconds for images
                        onclone: function(clonedDoc) {
                            // Hide buttons in cloned document
                            const clonedButtons = clonedDoc.querySelectorAll('.tear-sheet-actions');
                            clonedButtons.forEach(function(btn) {
                                btn.style.display = 'none';
                            });
                        }
                    }).then(function(canvas) {
                        // Restore original image sources
                        images.forEach(function(img, index) {
                            if (originalSources[index]) {
                                img.src = originalSources[index];
                            }
                        });
                        return canvas;
                    });
                });
            }).then(function(canvas) {
                try {
                    const imgData = canvas.toDataURL('image/png', 1.0);
                    const pdf = new window.jspdf.jsPDF({
                        orientation: 'portrait',
                        unit: 'in',
                        format: 'letter'
                    });

                    const imgWidth = 8.5;
                    const pageHeight = 11;
                    const imgHeight = (canvas.height * imgWidth) / canvas.width;
                    let heightLeft = imgHeight;
                    let position = 0;

                    // Add first page
                    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                    heightLeft -= pageHeight;

                    // Add additional pages if content is taller than one page
                    while (heightLeft > 0) {
                        position = heightLeft - imgHeight;
                        pdf.addPage();
                        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                        heightLeft -= pageHeight;
                    }

                    // Save PDF
                    pdf.save(sanitizedTitle + '-tear-sheet.pdf');

                    // Reset button state
                    pdfButton.textContent = originalText;
                    pdfButton.disabled = false;
                    if (printButton) {
                        printButton.style.display = 'inline-block';
                    }
                } catch (error) {
                    console.error('Error creating PDF:', error);
                    pdfButton.textContent = originalText;
                    pdfButton.disabled = false;
                    if (printButton) {
                        printButton.style.display = 'inline-block';
                    }
                    alert('Error generating PDF. Please try using the Print button instead.');
                }
            }).catch(function(error) {
                console.error('Error generating PDF:', error);
                pdfButton.textContent = originalText;
                pdfButton.disabled = false;
                if (printButton) {
                    printButton.style.display = 'inline-block';
                }
                alert('Error generating PDF: ' + (error.message || 'Please try using the Print button instead.'));
            });
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initPDFGenerator);
    } else {
        initPDFGenerator();
    }
})();

